import {
  AUTOCOMPLETE_MAX_PREFIX_CHARS,
  AUTOCOMPLETE_MAX_SUFFIX_CHARS,
} from "@racler/shared";
import type { AutocompleteResponse } from "@racler/protocol";
import { loadEditorSettings } from "../editorSettings.ts";
import type { RelayClient } from "../relayClient.ts";

export interface AutocompleteStatus {
  enabled: boolean;
  unavailableReason: string | null;
}

type StatusListener = (status: AutocompleteStatus) => void;
const statusListeners = new Set<StatusListener>();

let currentStatus: AutocompleteStatus = {
  enabled: loadEditorSettings().autocomplete,
  unavailableReason: null,
};

export function getAutocompleteStatus(): AutocompleteStatus {
  return {
    unavailableReason: currentStatus.unavailableReason,
    enabled: loadEditorSettings().autocomplete,
  };
}

export function setAutocompleteStatus(patch: Partial<AutocompleteStatus>): void {
  const persistentEnabled = loadEditorSettings().autocomplete;
  currentStatus = {
    ...currentStatus,
    ...patch,
    enabled: patch.enabled !== undefined ? patch.enabled : persistentEnabled,
  };
  for (const fn of statusListeners) {
    try {
      fn(currentStatus);
    } catch {
      // ignore
    }
  }
}

export function subscribeAutocompleteStatus(fn: StatusListener): () => void {
  statusListeners.add(fn);
  return () => void statusListeners.delete(fn);
}

/** Parses `racler://<machineId>/<projectId>/<path>` */
export function parseModelUri(uri: { scheme: string; authority: string; path: string }): {
  machineId: string;
  projectId: string;
  path: string;
} | null {
  if (uri.scheme !== "racler") return null;
  const machineId = uri.authority;
  const pathParts = uri.path.replace(/^\//, "").split("/");
  if (pathParts.length < 2) return null;
  const projectId = pathParts[0]!;
  const relPath = pathParts.slice(1).join("/");
  return { machineId, projectId, path: relPath };
}

export function shouldTrigger(
  autocompleteEnabled: boolean,
  hasSelectedSuggestion: boolean,
  isDisposed: boolean,
): boolean {
  if (!autocompleteEnabled) return false;
  if (hasSelectedSuggestion) return false;
  if (isDisposed) return false;
  return true;
}

export function extractBoundedContext(
  fullPrefix: string,
  fullSuffix: string,
): { prefix: string; suffix: string } {
  return {
    prefix: fullPrefix.slice(-AUTOCOMPLETE_MAX_PREFIX_CHARS),
    suffix: fullSuffix.slice(0, AUTOCOMPLETE_MAX_SUFFIX_CHARS),
  };
}

export function isResponseValid(
  capturedVersionId: number,
  currentVersionId: number,
  isCancelled: boolean,
  res?: AutocompleteResponse,
): boolean {
  if (isCancelled) return false;
  if (capturedVersionId !== currentVersionId) return false;
  if (!res) return false;
  if (res.unavailable) return false;
  if (!res.text || !res.text.trim()) return false;
  return true;
}

/**
 * Distinguishes permanent capability/configuration unavailability from transient request failure.
 * Only permanent conditions may cause the UI to show "AI Unavailable".
 */
export function isPermanentUnavailableReason(reason?: string | null): boolean {
  if (!reason) return false;
  const lower = reason.toLowerCase();

  // Transient failures must NEVER trigger permanent unavailability:
  // - Gemini returns MAX_TOKENS with no visible text
  // - temporary provider/API error (e.g. rate limit, 500/503, etc.)
  // - network failure
  // - empty completion
  // - timeout
  // - malformed single response
  if (
    lower.includes("max_tokens") ||
    lower.includes("timeout") ||
    lower.includes("timed out") ||
    lower.includes("rate limit") ||
    lower.includes("429") ||
    lower.includes("network") ||
    lower.includes("connection") ||
    lower.includes("fetch") ||
    lower.includes("econn") ||
    lower.includes("etimedout") ||
    lower.includes("could not reach") ||
    lower.includes("500") ||
    lower.includes("502") ||
    lower.includes("503") ||
    lower.includes("504") ||
    lower.includes("server error") ||
    lower.includes("gemini error") ||
    lower.includes("empty completion") ||
    lower.includes("abort") ||
    lower.includes("malformed")
  ) {
    return false;
  }

  // Permanent conditions:
  // - no autocomplete model configured
  // - provider credential unavailable
  // - configured model/provider does not support autocomplete
  // - required provider configuration is missing
  // - monthly limit reached
  return (
    lower.includes("not configured") ||
    lower.includes("no autocomplete model") ||
    lower.includes("credential") ||
    lower.includes("auth") ||
    lower.includes("not support") ||
    lower.includes("missing") ||
    lower.includes("limit reached") ||
    lower.includes("limit exceeded") ||
    lower.includes("not found on machine") ||
    lower.includes("unknown provider") ||
    lower.includes("unknown model") ||
    lower.includes("ai is not enabled")
  );
}

export class AutocompleteController {
  private activeController: AbortController | null = null;
  private activeRequestId: string | null = null;
  private activeMachineId: string | null = null;
  private readonly relay: RelayClient;

  constructor(relay: RelayClient) {
    this.relay = relay;
  }

  startRequest(): { controller: AbortController; requestId: string } {
    this.cancelActive();
    const controller = new AbortController();
    const requestId = crypto.randomUUID();
    this.activeController = controller;
    this.activeRequestId = requestId;
    return { controller, requestId };
  }

  setMachineId(machineId: string): void {
    this.activeMachineId = machineId;
  }

  cancelActive(): void {
    if (this.activeController) {
      this.activeController.abort();
      this.activeController = null;
    }
    if (this.activeRequestId && this.activeMachineId) {
      this.relay.cancelAutocomplete(this.activeMachineId, this.activeRequestId);
      this.activeRequestId = null;
    }
  }

  cleanup(requestId: string): void {
    if (this.activeRequestId === requestId) {
      this.activeRequestId = null;
      this.activeController = null;
    }
  }
}
